import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useI18n, SUPPORTED_LANGS } from "@/lib/i18n";

export default function Navigation() {
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#services", label: t.nav.services },
    { href: "#spectrum", label: t.nav.spectrum },
    { href: "#accident", label: t.nav.accident },
    { href: "#team", label: t.nav.team },
    { href: "#references", label: t.nav.gallery },
    { href: "#contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-24 md:h-28 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src="/mp-logo.svg"
            alt="MP Carrosserie"
            className="h-16 md:h-20 lg:h-24 w-auto group-hover:scale-105 transition-transform"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-sm text-zinc-300 hover:text-white transition-colors uppercase tracking-wider font-medium"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div className="relative">
            <button
              data-testid="language-switcher-button"
              onClick={() => setLangOpen((s) => !s)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold text-zinc-300 hover:text-white border border-white/10 hover:border-white/25 rounded-md transition-colors"
            >
              {lang.toUpperCase()}
              <ChevronDown className="w-3 h-3" />
            </button>
            {langOpen && (
              <div
                data-testid="language-switcher-dropdown"
                className="absolute right-0 mt-2 w-28 bg-[#0e0e0e] border border-white/10 rounded-md overflow-hidden shadow-2xl"
                onMouseLeave={() => setLangOpen(false)}
              >
                {SUPPORTED_LANGS.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`lang-option-${l.code}`}
                    onClick={() => {
                      setLang(l.code);
                      setLangOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold transition-colors ${
                      lang === l.code
                        ? "bg-[#e52620] text-white"
                        : "text-zinc-300 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="#contact"
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center gap-2 bg-[#e52620] hover:bg-[#b51e18] text-white text-xs uppercase tracking-[0.15em] font-bold px-5 py-3 rounded-md transition-all active:scale-95"
          >
            {t.nav.cta}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((s) => !s)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
                onClick={() => setOpen(false)}
                className="text-zinc-300 hover:text-white uppercase tracking-wider text-sm font-medium"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-2 bg-[#e52620] hover:bg-[#b51e18] text-white text-sm uppercase tracking-wider font-bold px-5 py-3 rounded-md text-center"
              data-testid="mobile-nav-cta"
            >
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
